
import { UseFormReturn } from "react-hook-form";
import { FormValues } from "@/types/loan";
import { LoanAmountSection } from "./LoanAmountSection";
import { LoanTermSection } from "./LoanTermSection";
import { LoanCalculationSummary } from "./LoanCalculationSummary";
import { LoanPurposeField } from "./LoanPurposeField";
import { motion } from "framer-motion";
import { useEffect } from "react";

interface LoanDetailsSectionProps {
  form: UseFormReturn<FormValues>;
  loanAmount: number;
  setLoanAmount: (value: number) => void;
  loanTerm: number;
  setLoanTerm: (value: number) => void;
  monthlyPayment: number;
  totalPaid: number;
  interestAndFees: number;
  interest: number;
  monthlyFee?: number;
  setupFee?: number;
}

export const LoanDetailsSection = ({
  form,
  loanAmount,
  setLoanAmount,
  loanTerm,
  setLoanTerm,
  monthlyPayment,
  totalPaid,
  interestAndFees,
  interest,
  monthlyFee = 5,
  setupFee = 150
}: LoanDetailsSectionProps) => {
  // Set default loan type silently in the background
  useEffect(() => {
    // Ensure form value is set for analytics
    if (!form.getValues().loanType) {
      form.setValue("loanType", "kulutusluotto");
    }
  }, [form]);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="p-3 sm:p-4 bg-gradient-to-b from-blue-50 to-blue-50/60 rounded-xl border border-blue-100/80 shadow-sm w-full"
    >
      <h2 className="text-sm sm:text-base font-bold text-blue-700 mb-2 sm:mb-3 flex items-center">
        <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full mr-2"></span>
        <span className="truncate">Lainatiedot</span>
      </h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="col-span-1 sm:col-span-2">
          <LoanPurposeField form={form} className="mb-2 sm:mb-3" />
        </div>
        
        <div className="col-span-1">
          <LoanAmountSection 
            loanAmount={loanAmount} 
            setLoanAmount={setLoanAmount} 
          />
        </div>
        
        <div className="col-span-1">
          <LoanTermSection 
            loanTerm={loanTerm} 
            setLoanTerm={setLoanTerm} 
          />
        </div>
        
        <div className="col-span-1 sm:col-span-2 mt-1 sm:mt-2">
          <LoanCalculationSummary 
            monthlyPayment={monthlyPayment}
            totalPaid={totalPaid}
            interestAndFees={interestAndFees}
            interestRate={interest}
            monthlyFee={monthlyFee}
            setupFee={setupFee}
            showAsEstimate={true}
          />
        </div>
      </div>
    </motion.div>
  );
};
